import { useMemo, useState } from "react";
import type { EntitySeries } from "../lib/api";
import { fmt } from "./Viz";

/**
 * One row's weekly history.
 *
 * The hard problem is axes. Impressions run in the thousands, CTR in
 * hundredths, position from 1 to 100. They cannot share a scale, and more
 * than two axes is unreadable. So: the first two selected metrics own the
 * left and right axes and carry real tick labels; any others are drawn to
 * their own range as shape only, and the chart says so rather than implying
 * a scale it does not have.
 *
 * Position is inverted everywhere, so up always means better.
 */

type Key = "impressions" | "clicks" | "ctr" | "position";

interface Lane {
  key: Key;
  label: string;
  color: string;
  format: Parameters<typeof fmt>[1];
  lowerIsBetter?: boolean;
  /** Counts start at zero; rates start at their observed floor. */
  zeroBased?: boolean;
}

const LANES: Lane[] = [
  { key: "impressions", label: "Impressions", color: "#5b21b6", format: "int", zeroBased: true },
  { key: "clicks", label: "Clicks", color: "#2563eb", format: "int", zeroBased: true },
  { key: "ctr", label: "CTR", color: "#15803d", format: "percent" },
  { key: "position", label: "Position", color: "#a16207", format: "position", lowerIsBetter: true },
];

const W = 900, H = 260;
const M = { top: 20, right: 58, bottom: 34, left: 58 };
const PLOT_W = W - M.left - M.right;
const PLOT_H = H - M.top - M.bottom;

export function EntityDetail({ series, loading, error }: {
  series: EntitySeries | null;
  loading: boolean;
  error: string | null;
}) {
  const [selected, setSelected] = useState<Key[]>(["impressions", "clicks", "ctr", "position"]);
  const [hover, setHover] = useState<number | null>(null);

  const rows = series?.rows ?? [];

  /** Per-metric domain, padded so lines never touch the frame. */
  const domains = useMemo(() => {
    const out = {} as Record<Key, { min: number; max: number }>;
    for (const l of LANES) {
      const vals = rows.map((r) => r[l.key])
        .filter((v): v is number => v != null && Number.isFinite(v));
      if (!vals.length) { out[l.key] = { min: 0, max: 1 }; continue; }
      let min = l.zeroBased ? 0 : Math.min(...vals);
      let max = Math.max(...vals);
      if (min === max) { max = min + 1; }
      const pad = (max - min) * 0.08;
      out[l.key] = { min: l.zeroBased ? 0 : min - pad, max: max + pad };
    }
    return out;
  }, [rows]);

  if (loading) {
    return <div className="det det-state"><span className="spinner" />Loading history</div>;
  }
  if (error) return <div className="det det-state err">{error}</div>;
  if (!series) return null;
  if (rows.length < 2) {
    return <div className="det det-state">Only {rows.length} week of data. Not enough to plot a trend.</div>;
  }

  const active = LANES.filter((l) => selected.includes(l.key));
  const axisLeft = active[0] ?? null;
  const axisRight = active[1] ?? null;
  const shapeOnly = active.slice(2);

  const x = (i: number) => M.left + (i / (rows.length - 1)) * PLOT_W;
  const y = (k: Key, v: number) => {
    const { min, max } = domains[k];
    const norm = (v - min) / (max - min || 1);
    const up = LANES.find((l) => l.key === k)?.lowerIsBetter ? 1 - norm : norm;
    return M.top + (1 - up) * PLOT_H;
  };

  const idx = hover ?? rows.length - 1;

  // Four gridlines. Labels come from whichever metrics own the axes.
  const ticks = [0, 0.25, 0.5, 0.75, 1];
  const tickValue = (l: Lane, t: number) => {
    const { min, max } = domains[l.key];
    const frac = l.lowerIsBetter ? 1 - t : t;
    return min + frac * (max - min);
  };

  // At most six date labels, evenly spaced, never crowded.
  const step = Math.max(1, Math.ceil(rows.length / 6));
  const dateIdx = rows.map((_, i) => i).filter((i) => i % step === 0 || i === rows.length - 1);

  return (
    <div className="det">
      <div className="det-cards">
        {LANES.map((l) => {
          const on = selected.includes(l.key);
          const vals = rows.map((r) => r[l.key]).filter((v): v is number => v != null);
          const a = vals[0], b = vals[vals.length - 1];
          const delta = a != null && b != null && a !== 0 ? (b - a) / Math.abs(a) : null;
          const good = delta == null ? null : (l.lowerIsBetter ? delta < 0 : delta > 0);
          const shown = rows[idx]?.[l.key];
          return (
            <button
              key={l.key}
              className={`det-card ${on ? "on" : ""}`}
              style={on ? { borderColor: l.color, boxShadow: `inset 0 2px 0 ${l.color}` } : undefined}
              aria-pressed={on}
              onClick={() => setSelected((s) =>
                s.includes(l.key)
                  ? (s.length > 1 ? s.filter((k) => k !== l.key) : s)
                  : [...s, l.key])}
            >
              <span className="det-card-label">{l.label}</span>
              <span className="det-card-value num" style={on ? { color: l.color } : undefined}>
                {fmt(shown ?? b, l.format)}
              </span>
              {delta != null && (
                <span className={`det-card-delta ${good ? "up" : "down"}`}>
                  {delta > 0 ? "▲" : "▼"} {Math.abs(delta * 100).toFixed(0)}%
                </span>
              )}
            </button>
          );
        })}
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} width="100%" className="det-chart"
           role="img" aria-label="Weekly history"
           onMouseLeave={() => setHover(null)}
           onMouseMove={(e) => {
             const box = e.currentTarget.getBoundingClientRect();
             const rel = ((e.clientX - box.left) / box.width) * W;
             const i = Math.round(((rel - M.left) / PLOT_W) * (rows.length - 1));
             setHover(Math.max(0, Math.min(rows.length - 1, i)));
           }}>
        {/* gridlines and axis labels */}
        {ticks.map((t) => {
          const yy = M.top + (1 - t) * PLOT_H;
          return (
            <g key={t}>
              <line x1={M.left} y1={yy} x2={M.left + PLOT_W} y2={yy}
                    stroke="#efedf5" strokeWidth="1" />
              {axisLeft && (
                <text x={M.left - 8} y={yy + 3.5} fontSize="10" textAnchor="end"
                      fill={axisLeft.color} fontFamily="JetBrains Mono, monospace">
                  {fmt(tickValue(axisLeft, t), axisLeft.format)}
                </text>
              )}
              {axisRight && (
                <text x={M.left + PLOT_W + 8} y={yy + 3.5} fontSize="10"
                      fill={axisRight.color} fontFamily="JetBrains Mono, monospace">
                  {fmt(tickValue(axisRight, t), axisRight.format)}
                </text>
              )}
            </g>
          );
        })}

        <line x1={M.left} y1={M.top} x2={M.left} y2={M.top + PLOT_H} stroke="#e6e3ee" />
        <line x1={M.left + PLOT_W} y1={M.top} x2={M.left + PLOT_W} y2={M.top + PLOT_H}
              stroke="#e6e3ee" />
        <line x1={M.left} y1={M.top + PLOT_H} x2={M.left + PLOT_W} y2={M.top + PLOT_H}
              stroke="#d9d5e6" />

        {/* x axis */}
        {dateIdx.map((i) => (
          <g key={i}>
            <line x1={x(i)} y1={M.top + PLOT_H} x2={x(i)} y2={M.top + PLOT_H + 4}
                  stroke="#d9d5e6" />
            <text x={x(i)} y={M.top + PLOT_H + 17} fontSize="10" textAnchor="middle"
                  fill="#8b869e" fontFamily="JetBrains Mono, monospace">
              {shortDate(rows[i].week)}
            </text>
          </g>
        ))}

        {hover != null && (
          <line x1={x(hover)} y1={M.top} x2={x(hover)} y2={M.top + PLOT_H}
                stroke="#b9a9e8" strokeWidth="1" strokeDasharray="3 3" />
        )}

        {active.map((l) => {
          const d = rows.map((r, i) => {
            const v = r[l.key];
            return v == null ? null : `${i ? "L" : "M"}${x(i).toFixed(1)},${y(l.key, v).toFixed(1)}`;
          }).filter(Boolean).join(" ");
          const dashed = shapeOnly.some((s) => s.key === l.key);
          return (
            <g key={l.key}>
              <path d={d} fill="none" stroke={l.color} strokeWidth="2"
                    strokeLinejoin="round" strokeLinecap="round"
                    strokeDasharray={dashed ? "5 4" : undefined}
                    vectorEffect="non-scaling-stroke" />
              {hover != null && rows[hover][l.key] != null && (
                <circle cx={x(hover)} cy={y(l.key, rows[hover][l.key] as number)}
                        r="4" fill="#fff" stroke={l.color} strokeWidth="2" />
              )}
            </g>
          );
        })}

        {hover != null && (
          <text x={x(hover)} y={M.top - 7} fontSize="10" textAnchor="middle"
                fill="#5b21b6" fontFamily="JetBrains Mono, monospace">
            {rows[hover].week}
          </text>
        )}
      </svg>

      <div className="det-axes">
        {axisLeft && <span><i style={{ background: axisLeft.color }} />left axis: {axisLeft.label}</span>}
        {axisRight && <span><i style={{ background: axisRight.color }} />right axis: {axisRight.label}</span>}
        {shapeOnly.length > 0 && (
          <span className="muted">
            dashed lines have no axis and show shape only
          </span>
        )}
        <span className="muted">position is inverted, so up is better</span>
      </div>

      {series.related.length > 0 && (
        <div className="det-rel">
          <div className="det-rel-title">
            {series.dim === "query" ? "Pages Google served for this query" : "Queries this page appears for"}
          </div>
          <ul>
            {series.related.map((r) => (
              <li key={r.name}>
                <span className="det-rel-name" title={r.name}>{r.name}</span>
                <span className="num det-rel-val">
                  {r.impressions.toLocaleString()}<em>impr</em>
                  {r.clicks.toLocaleString()}<em>clicks</em>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/** 2026-03-09 becomes Mar 9. Full date stays available on hover. */
function shortDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return iso;
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${months[Number(m[2]) - 1]} ${Number(m[3])}`;
}
